/**
 * `memberapp/team/peopleModel.ts` — the pure facts behind the lead's phone
 * People screen (#2071, lead phone app B6; design note
 * `docs/agent-prompts/design-lead-in-miniapp-2026-10-10.md` §3 Option B,
 * §4). No React, no I/O: unit-tested in
 * `web/tests/memberapp/PeopleScreen.test.tsx`.
 *
 * One line per person from three reads the laptop's People page already
 * makes — the members list, the Telegram status read (#2072) and the
 * messages read (#2077) — so the phone never words a person differently:
 * the status pill is `memberStatusPill`, the Telegram chip is
 * `TelegramStatusText`, the message line is the laptop's "Message · ‹day›".
 *
 * #3065 — People by level, with dates (design note
 * `docs/agent-prompts/design-phone-team-round-2026-11-04.md`, slice 3). Each
 * line also carries the level, the ward now with its last day, the next
 * ward, and the first and last day on the team, from the postings read. The
 * level chips, the "Next 8 weeks" list and the two filters are pure helpers
 * here too.
 */
import type { TeamMessage } from "../../api/useMemberMessages";
import type { MemberTelegramStatus } from "../../api/useTelegramLink";
import { dayBefore, postingCovers, shortDayMonth } from "../../lib/peopleNextChange";
import { addDaysIso } from "../../lib/rosterDatesWindow";
import { levelShortLabel } from "../../lib/weekSheet/weekSheetModel";
import { type MemberStatusTone, memberStatusPill } from "../../routes/team/memberStatusPill";
import type { MemberRow } from "../../routes/team/types";
import { placeTitle } from "./teamTodayModel";

/** The People screen, from Team home's header link. */
export const TEAM_PEOPLE_PATH = "/me/team/people";
/** The `?show=` value Team home's "N messages from members" row opens. */
export const PEOPLE_UNREAD_VIEW = "unread";
export const TEAM_PEOPLE_UNREAD_PATH = `${TEAM_PEOPLE_PATH}?show=${PEOPLE_UNREAD_VIEW}`;

/** One person's messages to the lead, stacked over People. */
export function teamPersonPath(membershipId: string): string {
  return `${TEAM_PEOPLE_PATH}/${membershipId}`;
}

export const PEOPLE_LOADING = "Reading your team…";
export const PEOPLE_ERROR = "Couldn't load the people on this team right now.";
export const PEOPLE_NO_UNREAD = "No unread messages.";

/** The quiet last line: the phone does not invite, edit or remove. */
export const PEOPLE_LAPTOP_LINE = "Invite, edit and remove people on a laptop.";
/**
 * #2071 S2 — the same line when the phone can send a Telegram join link (the
 * one invite the phone makes: sharing a link is a phone job).
 */
export const PEOPLE_LAPTOP_LINE_TELEGRAM = "Edit, remove and invite by email on a laptop.";

/** #2071 S2 — the share sheet's words around a join link. */
export function joinShareText(name: string): string {
  return `Hi ${name}, this is our roster app on Telegram. Tap the link, then Start.`;
}

/**
 * The stacked-screen chrome (title + default back) for the two B6 paths,
 * read by `memberScreenMetaForPath`. `null` for any other path.
 */
export function teamPeopleScreenMeta(path: string): { title: string; backTo: string } | null {
  if (path === TEAM_PEOPLE_PATH) return { title: "People", backTo: "/me/team" };
  // #3065 — the person screen: a fixed title, the name on its first line.
  if (/^\/me\/team\/people\/[^/]+$/.test(path)) {
    return { title: "Person", backTo: TEAM_PEOPLE_PATH };
  }
  return null;
}

export interface PersonLine {
  id: string;
  name: string;
  status: { label: string; tone: MemberStatusTone };
  /**
   * The Telegram chip. `undefined` = no chip (the team has Telegram off);
   * `null` = the chip, nothing known for this person ("Not connected").
   */
  telegram: MemberTelegramStatus | null | undefined;
  /** When they last wrote to the lead; null = never (or not readable). */
  latestMessageAt: string | null;
  /** Their messages nobody has marked read yet. */
  unread: number;
  /**
   * #2071 S2 — a claimable placeholder (no account, active): the lead's phone
   * offers **Invite via Telegram** with **Share** on it.
   */
  placeholder: boolean;
  /** #2071 S2 (J5) — no login: Telegram is their only way in (or `null`). */
  hasLogin: boolean | null;
  /** #3065 — the level key (`MemberRow.tier`). */
  tier: string;
  /** #3065 — the ward (station) of the posting that covers today, or null. */
  station: string | null;
  /** #3065 — that posting's last day (inclusive), or null when it has no end. */
  stationUntil: string | null;
  /** #3065 — the next ward after this one (a posting that starts later), or null. */
  nextStation: string | null;
  /** #3065 — the first day of the next ward. */
  nextStationFrom: string | null;
  /** #3065 — the first day on the team, or null when not on record. */
  joinedAt: string | null;
  /** #3065 — the last day on the team, or null when not on record. */
  leftAt: string | null;
  /** #3065 — the first change in the next 8 weeks (the row's date tag), or null. */
  change: PeopleChange | null;
}

/** The postings fields the phone reads (`GET /api/teams/{id}/postings`). */
export interface PeoplePosting {
  id: string;
  membership_id: string;
  station: string;
  valid_from: string;
  /** EXCLUSIVE end, as the API stores it; `null` = no end. */
  valid_to_exclusive: string | null;
}

/** #3065 — one line of "Next 8 weeks". */
export interface PeopleChange {
  date: string;
  kind: "leaves" | "joins" | "moves";
  membershipId: string;
  name: string;
  tier: string;
  /** "last day", "joins" or "51 to 56". */
  text: string;
}

/** #3065 — how far ahead "Next 8 weeks" and the row tags look. */
export const CHANGES_AHEAD_WEEKS = 8;

export interface PeopleFacts {
  members: MemberRow[];
  /** Status per membership id; `undefined` when the team has Telegram off. */
  telegram?: Record<string, MemberTelegramStatus> | undefined;
  /** The team messages read; `undefined` for an observer (the API refuses). */
  messages?: { messages: TeamMessage[]; latest_by_membership: Record<string, string> } | undefined;
  /** Only people with an unread message, newest first. */
  unreadOnly?: boolean;
  /** #3065 — the postings read; omitted = no ward on any line. */
  postings?: readonly PeoplePosting[] | undefined;
  /** #3065 — ISO today, for the ward now and the changes ahead. */
  today?: string | undefined;
}

/**
 * The laptop's order — people with an account, then the manual placeholders,
 * each A–Z ignoring case (#2239) — or, filtered to unread, the people with an unread message, the most
 * recent writer first.
 */
export function buildPeopleLines({
  members,
  telegram,
  messages,
  unreadOnly = false,
  postings = [],
  today = "",
}: PeopleFacts): PersonLine[] {
  const unreadBy = new Map<string, number>();
  for (const m of messages?.messages ?? []) {
    if (!m.read_at) unreadBy.set(m.membership_id, (unreadBy.get(m.membership_id) ?? 0) + 1);
  }
  const latest = messages?.latest_by_membership ?? {};
  const postingsBy = groupPostings(postings);
  const firstChange = new Map<string, PeopleChange>();
  if (today) {
    for (const c of changesAhead(members, postings, today)) {
      if (!firstChange.has(c.membershipId)) firstChange.set(c.membershipId, c);
    }
  }
  const line = (m: MemberRow): PersonLine => ({
    id: m.id,
    name: m.display_name_in_team,
    status: memberStatusPill(m),
    telegram: telegram ? (telegram[m.id] ?? null) : undefined,
    latestMessageAt: latest[m.id] ?? null,
    unread: unreadBy.get(m.id) ?? 0,
    placeholder: m.account_id === null && m.status === "active",
    hasLogin: m.has_login ?? null,
    tier: m.tier,
    ...wardFacts(postingsBy.get(m.id) ?? [], today),
    joinedAt: m.joined_at ?? null,
    leftAt: m.left_at ?? null,
    change: firstChange.get(m.id) ?? null,
  });
  if (unreadOnly) {
    return members
      .map(line)
      .filter((p) => p.unread > 0)
      .sort((a, b) => (b.latestMessageAt ?? "").localeCompare(a.latestMessageAt ?? ""));
  }
  // #2239 — A–Z whatever the case: the read came back capitals first
  // ("ZHANG …" above "adeline …").
  const byName = (a: MemberRow, b: MemberRow) =>
    a.display_name_in_team.localeCompare(b.display_name_in_team, undefined, {
      sensitivity: "base",
    });
  return [
    ...members.filter((m) => m.account_id !== null).sort(byName),
    ...members.filter((m) => m.account_id === null).sort(byName),
  ].map(line);
}

/** "9 people · 3 on Telegram" — the quiet line over the list. */
export function peopleSummary(lines: PersonLine[], telegramOn: boolean): string {
  const people = `${lines.length} ${lines.length === 1 ? "person" : "people"}`;
  if (!telegramOn) return people;
  const connected = lines.filter((p) => p.telegram?.status === "connected").length;
  return `${people} · ${connected} on Telegram`;
}

// ─── #3065 — levels, wards and dates ─────────────────────────────────

function groupPostings(postings: readonly PeoplePosting[]): Map<string, PeoplePosting[]> {
  const by = new Map<string, PeoplePosting[]>();
  for (const p of postings) {
    const list = by.get(p.membership_id);
    if (list) list.push(p);
    else by.set(p.membership_id, [p]);
  }
  for (const list of by.values()) list.sort((a, b) => a.valid_from.localeCompare(b.valid_from));
  return by;
}

/** The posting's inclusive last day, or null when it has no end. */
export function postingLastDay(p: Pick<PeoplePosting, "valid_to_exclusive">): string | null {
  return p.valid_to_exclusive ? dayBefore(p.valid_to_exclusive) : null;
}

/** The ward now (the latest-starting posting that covers today) and the next ward. */
function wardFacts(
  mine: readonly PeoplePosting[],
  today: string,
): Pick<PersonLine, "station" | "stationUntil" | "nextStation" | "nextStationFrom"> {
  const none = { station: null, stationUntil: null, nextStation: null, nextStationFrom: null };
  if (!today) return none;
  const covering = mine.filter((p) => postingCovers(p, today));
  const now = covering[covering.length - 1] ?? null;
  const next =
    mine.find((p) => p.valid_from > today && p.station !== (now?.station ?? null)) ?? null;
  return {
    station: now?.station ?? null,
    stationUntil: now ? postingLastDay(now) : null,
    nextStation: next?.station ?? null,
    nextStationFrom: next?.valid_from ?? null,
  };
}

/** #3065 — one level chip: `key` null is "All". */
export interface LevelChip {
  key: string | null;
  label: string;
  count: number;
}

/**
 * #3078 — the name of a level key the setup does not name: the key as a word
 * ("lead" → "Lead", the seat a lead holds with no team level). Before, the raw
 * key showed, and the lead's chip read lowercase "lead 1".
 */
function unnamedLevelLabel(key: string): string {
  return key.charAt(0).toUpperCase() + key.slice(1);
}

/**
 * "All 42 · REG 8 · MO 14 · HO 20": All, then each level that has people, in
 * the team's setup order with its short label. A level the setup does not
 * name comes after, under its key as a word (`unnamedLevelLabel`).
 */
export function levelChips(
  lines: readonly Pick<PersonLine, "tier">[],
  tiers: readonly { key: string; label: string }[],
): LevelChip[] {
  const counts = new Map<string, number>();
  for (const l of lines) if (l.tier) counts.set(l.tier, (counts.get(l.tier) ?? 0) + 1);
  const chips: LevelChip[] = [{ key: null, label: "All", count: lines.length }];
  for (const t of tiers) {
    const count = counts.get(t.key) ?? 0;
    if (count > 0) chips.push({ key: t.key, label: levelShortLabel(t.label), count });
  }
  const named = new Set(tiers.map((t) => t.key));
  const others = [...counts.keys()].filter((k) => !named.has(k)).sort();
  for (const k of others) {
    chips.push({ key: k, label: unnamedLevelLabel(k), count: counts.get(k) ?? 0 });
  }
  return chips;
}

/** #3065 — one level's people under a head row (the List with All). */
export interface LevelGroup {
  key: string;
  label: string;
  lines: PersonLine[];
}

/**
 * With All, the list has a head row per level: the levels in setup order with
 * their short labels, then any level the setup does not name (its key as a word), then
 * people with no level ("No level"). Each group keeps the list's own order.
 * Empty levels drop out.
 */
export function levelGroups(
  lines: readonly PersonLine[],
  tiers: readonly { key: string; label: string }[],
): LevelGroup[] {
  const by = new Map<string, PersonLine[]>();
  for (const l of lines) {
    const key = l.tier || "";
    const list = by.get(key);
    if (list) list.push(l);
    else by.set(key, [l]);
  }
  const groups: LevelGroup[] = [];
  for (const t of tiers) {
    const mine = by.get(t.key);
    if (mine) groups.push({ key: t.key, label: levelShortLabel(t.label), lines: mine });
  }
  const named = new Set(tiers.map((t) => t.key));
  const others = [...by.keys()].filter((k) => k && !named.has(k)).sort();
  for (const k of others) {
    groups.push({ key: k, label: unnamedLevelLabel(k), lines: by.get(k) ?? [] });
  }
  const none = by.get("");
  if (none) groups.push({ key: "", label: "No level", lines: none });
  return groups;
}

/** The short label of a level key, from the setup ("MO"), else the key as a word ("Lead"). */
export function levelLabel(tier: string, tiers: readonly { key: string; label: string }[]): string {
  const found = tiers.find((t) => t.key === tier);
  return found ? levelShortLabel(found.label) : unnamedLevelLabel(tier);
}

const CHANGE_RANK: Record<PeopleChange["kind"], number> = { leaves: 0, joins: 1, moves: 2 };

/**
 * "Next 8 weeks": each last day on the team, first day on the team and ward
 * move after today and within `weeks`, in date order. A move is a posting
 * that starts where the person's posting of the day before was another ward.
 */
export function changesAhead(
  members: readonly MemberRow[],
  postings: readonly PeoplePosting[],
  today: string,
  weeks: number = CHANGES_AHEAD_WEEKS,
): PeopleChange[] {
  const last = addDaysIso(today, weeks * 7);
  const within = (d: string | null | undefined): d is string => !!d && d > today && d <= last;
  const postingsBy = groupPostings(postings);
  const out: PeopleChange[] = [];
  for (const m of members) {
    if (m.status === "invited" || m.status === "left") continue;
    const who = { membershipId: m.id, name: m.display_name_in_team, tier: m.tier };
    if (within(m.left_at)) out.push({ date: m.left_at, kind: "leaves", text: "last day", ...who });
    if (within(m.joined_at)) out.push({ date: m.joined_at, kind: "joins", text: "joins", ...who });
    const mine = postingsBy.get(m.id) ?? [];
    for (const p of mine) {
      if (!within(p.valid_from)) continue;
      if (m.left_at && m.left_at < p.valid_from) continue;
      const before = dayBefore(p.valid_from);
      const prior = mine.filter((q) => q.id !== p.id && postingCovers(q, before));
      const from = prior[prior.length - 1];
      if (!from || from.station === p.station) continue;
      out.push({
        date: p.valid_from,
        kind: "moves",
        text: `${from.station} to ${p.station}`,
        ...who,
      });
    }
  }
  out.sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      CHANGE_RANK[a.kind] - CHANGE_RANK[b.kind] ||
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
  );
  return out;
}

/** The row's date tag: "Last day 29 Nov", "Joins 30 Nov", "Moves 7 Dec". */
export function changeTagText(change: PeopleChange | null): string | null {
  if (!change) return null;
  const day = shortDayMonth(change.date);
  if (change.kind === "leaves") return `Last day ${day}`;
  if (change.kind === "joins") return `Joins ${day}`;
  return `Moves ${day}`;
}

/**
 * The year a phone date carries: none for a day in this year, a day up to
 * 300 days ahead, or a day up to 60 days back; else " 2024".
 */
function yearSuffix(iso: string, today: string): string {
  if (!today || iso.slice(0, 4) === today.slice(0, 4)) return "";
  if (iso >= addDaysIso(today, -60) && iso <= addDaysIso(today, 300)) return "";
  return ` ${iso.slice(0, 4)}`;
}

/** "Sun 3 Jan": a phone day, with the year only when it is far (`yearSuffix`). */
export function phoneDay(iso: string, today: string): string {
  const [y = 1970, m = 1, d = 1] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const weekday = dt.toLocaleDateString("en-GB", { weekday: "short", timeZone: "UTC" });
  const month = dt.toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" });
  return `${weekday} ${d} ${month}${yearSuffix(iso, today)}`;
}

/** A posting's dates on the person screen: "3 Aug – 29 Nov", or "from 3 Aug". */
export function postingSpanText(
  p: Pick<PeoplePosting, "valid_from" | "valid_to_exclusive">,
  today: string,
): string {
  const day = (iso: string) => `${shortDayMonth(iso)}${yearSuffix(iso, today)}`;
  const last = postingLastDay(p);
  return last ? `${day(p.valid_from)} – ${day(last)}` : `from ${day(p.valid_from)}`;
}

/**
 * The row's second line: "Ward 31 · to Sun 3 Jan", "Ward 51 · 56 from 7 Dec",
 * or "Ward 51" when the ward ends with the person's last day (the tag says
 * it). A future joiner with a first ward reads "Ward 56 from 30 Nov". Null
 * when the person has no ward now or ahead.
 */
export function wardLine(
  line: Pick<PersonLine, "station" | "stationUntil" | "nextStation" | "nextStationFrom" | "leftAt">,
  today: string,
): string | null {
  if (!line.station) {
    return line.nextStation && line.nextStationFrom
      ? `${placeTitle(line.nextStation)} from ${shortDayMonth(line.nextStationFrom)}`
      : null;
  }
  const here = placeTitle(line.station);
  if (line.nextStation && line.nextStationFrom) {
    return `${here} · ${line.nextStation} from ${shortDayMonth(line.nextStationFrom)}`;
  }
  if (line.stationUntil && !(line.leftAt && line.leftAt <= line.stationUntil)) {
    return `${here} · to ${phoneDay(line.stationUntil, today)}`;
  }
  return here;
}

/** #3065 — the List's two filters. Both may apply at once. */
export function filterLines(
  lines: readonly PersonLine[],
  { level = null, telegramOff = false }: { level?: string | null; telegramOff?: boolean },
): PersonLine[] {
  return lines.filter(
    (l) =>
      (level === null || l.tier === level) &&
      (!telegramOff || (l.telegram !== undefined && l.telegram?.status !== "connected")),
  );
}

/** #3065 — how many lines are not on Telegram (the "Not on Telegram" chip). */
export function notOnTelegramCount(lines: readonly PersonLine[]): number {
  return filterLines(lines, { telegramOff: true }).length;
}
